import React, { useEffect, useState } from 'react';

function FoodGrn({ token, selectedProperty }) {
  const [pos, setPos] = useState([]);
  const [stores, setStores] = useState([]);
  const [poDetail, setPoDetail] = useState(null);
  const [grns, setGrns] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ po_id: '', store_id: '', lines: [] });

  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    ...extra
  });

  const parseResponse = async (res) => {
    const raw = await res.text();
    try {
      const json = raw ? JSON.parse(raw) : {};
      return { data: json, message: json?.error || '' };
    } catch {
      return { data: {}, message: raw || '' };
    }
  };

  const loadBase = async () => {
    if (!chainId || !propertyId) return;
    try {
      const [poRes, storeRes, grnRes] = await Promise.all([
        fetch('/api/v1/food/po', { headers: headers() }),
        fetch('/api/v1/food/masters/stores', { headers: headers() }),
        fetch('/api/v1/food/grn', { headers: headers() })
      ]);
      const po = await parseResponse(poRes);
      const store = await parseResponse(storeRes);
      const grn = await parseResponse(grnRes);
      if (!poRes.ok) return setError(po.message || 'Failed to load POs');
      if (!storeRes.ok) return setError(store.message || 'Failed to load stores');
      if (!grnRes.ok) return setError(grn.message || 'Failed to load GRNs');
      setPos(Array.isArray(po.data) ? po.data : []);
      setStores(Array.isArray(store.data) ? store.data : []);
      setGrns(Array.isArray(grn.data) ? grn.data : []);
    } catch {
      setError('Network error while loading GRN module');
    }
  };

  useEffect(() => {
    if (!chainId || !propertyId) {
      setError('Select a property first from Hotel Config');
      return;
    }
    setError('');
    setMessage('');
    loadBase();
  }, [token, chainId, propertyId]);

  const loadPoDetail = async (poId) => {
    if (!poId) {
      setPoDetail(null);
      setForm((prev) => ({ ...prev, po_id: '', lines: [] }));
      return;
    }
    try {
      const res = await fetch(`/api/v1/food/po/${poId}`, { headers: headers() });
      const { data, message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Failed to load PO detail');
      setPoDetail(data);
      setForm((prev) => ({
        ...prev,
        po_id: poId,
        lines: (Array.isArray(data.items) ? data.items : []).map((i) => ({
          po_item_id: i.id,
          item_id: i.item_id,
          item_name: i.item_name,
          ordered_qty: Number(i.qty || 0),
          unit_cost: Number(i.unit_cost || 0),
          received_qty: '',
          accepted_qty: '',
          rejected_qty: '0',
          batch_no: '',
          expiry_date: ''
        }))
      }));
    } catch {
      setError('Network error while loading PO detail');
    }
  };

  const setLine = (idx, patch) => {
    setForm((prev) => ({ ...prev, lines: prev.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  };

  const postGrn = async () => {
    setError('');
    setMessage('');
    if (!form.po_id) return setError('Select PO');
    if (!form.store_id) return setError('Select store');
    const lines = form.lines
      .map((l) => ({
        po_item_id: l.po_item_id,
        item_id: l.item_id,
        ordered_qty: Number(l.ordered_qty || 0),
        unit_cost: Number(l.unit_cost || 0),
        received_qty: Number(l.received_qty || 0),
        accepted_qty: Number(l.accepted_qty || 0),
        rejected_qty: Number(l.rejected_qty || 0),
        batch_no: l.batch_no || null,
        expiry_date: l.expiry_date || null
      }))
      .filter((l) => l.received_qty > 0);
    if (!lines.length) return setError('Enter received quantity for at least one line');
    try {
      const res = await fetch('/api/v1/food/grn', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ po_id: form.po_id, store_id: form.store_id, lines })
      });
      const { data, message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'GRN post failed');
      setMessage(`GRN #${data.grn_no} posted`);
      setPoDetail(null);
      setForm({ po_id: '', store_id: '', lines: [] });
      loadBase();
    } catch {
      setError('Network error while posting GRN');
    }
  };

  const inputStyle = { padding: '0.5rem', border: '1px solid #dcdde1', borderRadius: '6px' };

  return (
    <div className="page-shell">
      <h2 style={{ color: '#2f855a' }}>Food Management - Goods Receiving (GRN)</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      <div className="filter-bar">
        <h3>PO-based Receiving</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem', marginBottom: '0.7rem' }}>
          <select value={form.po_id} onChange={(e) => loadPoDetail(e.target.value)} style={inputStyle}>
            <option value="">Select PO</option>
            {pos.map((p) => <option key={p.id} value={p.id}>{`PO #${p.po_no} - ${p.supplier_name || ''} [${p.status}]`}</option>)}
          </select>
          <select value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })} style={inputStyle}>
            <option value="">Select Store</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
          </select>
        </div>
        {poDetail && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
              <thead>
                <tr style={{ background: '#2f855a', color: '#fff' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Item</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Ordered</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Received</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Accepted</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Rejected</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Batch No</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Expiry</th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((l, idx) => (
                  <tr key={l.po_item_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                    <td style={{ padding: '0.5rem' }}>{l.item_name}</td>
                    <td style={{ padding: '0.5rem' }}>{Number(l.ordered_qty || 0).toFixed(3)}</td>
                    <td style={{ padding: '0.5rem' }}><input type="number" min="0" step="0.001" value={l.received_qty} onChange={(e) => setLine(idx, { received_qty: e.target.value })} style={inputStyle} /></td>
                    <td style={{ padding: '0.5rem' }}><input type="number" min="0" step="0.001" value={l.accepted_qty} onChange={(e) => setLine(idx, { accepted_qty: e.target.value })} style={inputStyle} /></td>
                    <td style={{ padding: '0.5rem' }}><input type="number" min="0" step="0.001" value={l.rejected_qty} onChange={(e) => setLine(idx, { rejected_qty: e.target.value })} style={inputStyle} /></td>
                    <td style={{ padding: '0.5rem' }}><input value={l.batch_no} onChange={(e) => setLine(idx, { batch_no: e.target.value })} style={inputStyle} /></td>
                    <td style={{ padding: '0.5rem' }}><input type="date" value={l.expiry_date} onChange={(e) => setLine(idx, { expiry_date: e.target.value })} style={inputStyle} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ marginTop: '0.7rem' }}>
          <button onClick={postGrn} className="btn-success" style={{ padding: '0.5rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Post GRN & Update Stock</button>
        </div>
      </div>

      <div className="filter-bar">
        <h3>GRN History</h3>
        {grns.length === 0 ? <p>No GRN records</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#2f855a', color: '#fff' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>GRN No</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>PO No</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Store</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Accepted Qty</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Rejected Qty</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {grns.map((g) => (
                <tr key={g.id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                  <td style={{ padding: '0.5rem' }}>{g.grn_no}</td>
                  <td style={{ padding: '0.5rem' }}>{g.po_no || '-'}</td>
                  <td style={{ padding: '0.5rem' }}>{g.store_name || '-'}</td>
                  <td style={{ padding: '0.5rem' }}>{Number(g.accepted_qty || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.5rem' }}>{Number(g.rejected_qty || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.5rem' }}>{g.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default FoodGrn;
